import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import DataManagementTools from '@/components/compliance/DataManagementTools';
import SecurityAuditDashboard from '@/components/security/SecurityAuditDashboard';
import LegalComplianceTools from '@/components/compliance/LegalComplianceTools';
import { Shield, Scale, Database, AlertTriangle, CheckCircle } from 'lucide-react';

const ComplianceManager = () => {
  const [activeTab, setActiveTab] = useState('data-management');

  const getFeatureStatus = (feature: string) => {
    // ... keep existing code (statuses etc) the same ...
    const statuses = {
      'data-management': 'active',
      'security-monitoring': 'active',
      'legal-compliance': 'active',
      'gdpr-compliance': 'active',
      'security-audit': 'active',
      'copyright-tracking': 'active'
    };
    return statuses[feature as keyof typeof statuses] || 'inactive';
  };

  const StatusBadge = ({ status }: { status: string }) => {
    if (status === 'active') {
      return (
        <Badge variant="default" className="text-green-600 bg-white">
          <CheckCircle className="h-3 w-3 mr-1" />
          Active
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-black">
        <AlertTriangle className="h-3 w-3 mr-1" />
        Inactive
      </Badge>
    );
  };

  return (
    <div className="space-y-6 pt-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Legal & Compliance Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3 mb-6">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Database className="h-5 w-5 text-black" />
                    <span className="font-medium">GDPR & Data Management</span>
                  </div>
                  <StatusBadge status={getFeatureStatus('data-management')} />
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-black">
                  User data export, deletion, and anonymization tools for GDPR compliance
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-black" />
                    <span className="font-medium">Security Monitoring</span>
                  </div>
                  <StatusBadge status={getFeatureStatus('security-monitoring')} />
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-black">
                  Enhanced security event logging and suspicious activity detection
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Scale className="h-5 w-5 text-black" />
                    <span className="font-medium">Legal Compliance</span>
                  </div>
                  <StatusBadge status={getFeatureStatus('legal-compliance')} />
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-black">
                  Copyright tracking, attribution management, and terms compliance
                </p>
              </CardContent>
            </Card>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="data-management" className="flex items-center gap-2">
                <Database className="h-4 w-4" />
                Data Management
              </TabsTrigger>
              <TabsTrigger value="security-audit" className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Security Audit
              </TabsTrigger>
              <TabsTrigger value="legal-compliance" className="flex items-center gap-2">
                <Scale className="h-4 w-4" />
                Legal Compliance
              </TabsTrigger>
            </TabsList>

            <TabsContent value="data-management" className="mt-6">
              <DataManagementTools />
            </TabsContent>

            <TabsContent value="security-audit" className="mt-6">
              <SecurityAuditDashboard />
            </TabsContent>

            <TabsContent value="legal-compliance" className="mt-6">
              <LegalComplianceTools />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default ComplianceManager;
